// Admin > Warehouse > Inbound — every inbound pallet label for a warehouse.
//
// Pallets are NOT inventory (see palletReceiving.controller.js): this page
// reads the inbound_pallets table only. It offers:
//   • summary tiles (to place / in bays / in storage / missing / today /
//     oldest waiting),
//   • warehouse pills, stock-type + status pills, search, group-by-location,
//   • age colouring from the warehouse's amber/red thresholds (Settings >
//     Goods In > Inbound settings),
//   • row click → full history (every hop with source + who),
//   • admin actions: reprint, void (pending only), mark found at a
//     location, force-process,
//   • CSV export of the current filter set (BE-generated).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, RefreshCw, X, Printer, Trash2, MapPin, CheckCircle2, ChevronRight, Package } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import FilterPills from "./admin/FilterPills";
import {
    silentPrintBarcode,
    silentPrint,
    buildPalletLabel,
    inboundLabelName,
    browserPrint,
} from "./helpers/printAgent";
import { submitNetworkPrintJob, getSavedNetworkPrinter } from "./helpers/networkPrint";
import "../styles/inbound-admin.css";

type PalletStatus = "pending" | "placed" | "processed" | "voided";

export interface PalletRow {
    pallet_id: string;
    warehouse: string;
    status: PalletStatus;
    stock_type: string | null;
    supplier_name?: string | null;
    daily_seq: number;
    location_id: string | null;
    location_name: string | null;
    location_type: string | null;
    created_at: string;
    created_by_name: string | null;
    placed_at: string | null;
    location_since: string | null;
    last_seen_at: string | null;
    last_seen_by_name: string | null;
    missing_at: string | null;
    processed_at: string | null;
    processed_by_name: string | null;
    processed_location_id: string | null;
    processed_location_name: string | null;
    voided_at: string | null;
}

interface Summary {
    pending: number;
    in_lanes: number;
    in_storage: number;
    missing: number;
    processed_today: number;
    oldest_placed_at: string | null;
}

interface HistoryEvent {
    at: string;
    kind: "printed" | "placed" | "moved" | "missing" | "processed" | "voided";
    source?: string;
    by_name?: string | null;
    location_id?: string | null;
    location_name?: string | null;
    from_location_id?: string | null;
    from_location_name?: string | null;
    stock_type?: string | null;
}

type StatusFilter = "" | "pending" | "placed" | "missing" | "processed" | "voided";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

const fmtWhen = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const readUser = () => {
    try {
        const raw = window.localStorage.getItem("user");
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const InboundAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const warehouseScope = useWarehouseScope();
    const user = useMemo(readUser, []);

    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [warehouse, setWarehouse] = useState<string>(user.warehouse || "");
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
    const [q, setQ] = useState("");
    const [groupByLocation, setGroupByLocation] = useState(false);

    const [rows, setRows] = useState<PalletRow[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [stockTypes, setStockTypes] = useState<Array<{ name: string; code: string }>>([]);
    const [thresholds, setThresholds] = useState<{ warn_days: number | null; alert_days: number | null }>({ warn_days: null, alert_days: null });
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    // Row-click history modal.
    const [history, setHistory] = useState<{ pallet: PalletRow; events: HistoryEvent[] } | null>(null);
    // "Mark found at…" modal.
    const [foundPrompt, setFoundPrompt] = useState<PalletRow | null>(null);
    const [foundLocation, setFoundLocation] = useState("");
    const foundInputRef = useRef<HTMLInputElement | null>(null);

    // Warehouse list from /settings (admin), like the other warehouse pages.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                const list = data.map(r => r.warehouse).sort();
                setWarehouses(list);
                setWarehouse(w => (w && list.includes(w) ? w : list[0] ?? ""));
            } catch { /* leave empty */ }
        })();
    }, []);

    // Without cross_warehouse the page stays on the person's own warehouse.
    useEffect(() => {
        if (!warehouseScope.canCross && warehouseScope.own && warehouse !== warehouseScope.own) setWarehouse(warehouseScope.own);
    }, [warehouseScope.canCross, warehouseScope.own, warehouse]);

    const buildQuery = useCallback(() => {
        const p = new URLSearchParams();
        p.set("warehouse", warehouse);
        if (statusFilter) p.set("status", statusFilter);
        if (typeFilter) p.set("type", typeFilter);
        if (q.trim()) p.set("q", q.trim());
        return p.toString();
    }, [warehouse, statusFilter, typeFilter, q]);

    const load = useCallback(async () => {
        if (!warehouse) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/pallet-receiving/admin/pallets?${buildQuery()}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setRows(Array.isArray(data.pallets) ? data.pallets : []);
            setSummary(data.summary ?? null);
            setStockTypes(Array.isArray(data.stockTypes) ? data.stockTypes : []);
            setThresholds(data.thresholds ?? { warn_days: null, alert_days: null });
        } catch {
            toast.error(t("server_error"), { toastId: "inbound-admin-load" });
        } finally {
            setLoading(false);
        }
    }, [warehouse, buildQuery, t]);

    // Debounce the search box; everything else reloads immediately.
    useEffect(() => {
        const h = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(h);
    }, [load, q]);

    useEffect(() => {
        if (foundPrompt) setTimeout(() => foundInputRef.current?.focus(), 0);
    }, [foundPrompt]);

    // ---- Age colouring ---------------------------------------------------

    const ageMs = (r: PalletRow) => Date.now() - new Date(r.created_at).getTime();
    const ageLabel = (r: PalletRow) => {
        const ms = ageMs(r);
        if (ms < DAY) return t("inbound_admin_age_hours", { hours: String(Math.max(0, Math.floor(ms / HOUR))) });
        return t("inbound_admin_age_days", { days: String(Math.floor(ms / DAY)) });
    };
    const ageClass = (r: PalletRow) => {
        if (r.status !== "placed") return "";
        const days = ageMs(r) / DAY;
        if (thresholds.alert_days != null && days >= thresholds.alert_days) return " inbound-admin-row--alert";
        if (thresholds.warn_days != null && days >= thresholds.warn_days) return " inbound-admin-row--warn";
        return "";
    };

    const statusOf = (r: PalletRow): StatusFilter | "missing" =>
        r.status === "placed" && r.missing_at ? "missing" : r.status;

    const statusLabel = (r: PalletRow) => t(`inbound_admin_status_${statusOf(r)}`);

    // ---- Actions -----------------------------------------------------------

    const post = (path: string, body: Record<string, unknown> = {}) => apiFetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, uid: user.uid, name: user.name, warehouse }),
    });

    const openHistory = async (r: PalletRow) => {
        try {
            const res = await apiFetch(`/pallet-receiving/admin/pallets/${encodeURIComponent(r.pallet_id)}/history`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setHistory({ pallet: data.pallet, events: Array.isArray(data.events) ? data.events : [] });
        } catch {
            toast.error(t("server_error"), { toastId: "inbound-admin-history" });
        }
    };

    const reprint = async (r: PalletRow) => {
        setBusy(r.pallet_id);
        try {
            const html = buildPalletLabel(r.pallet_id, {
                warehouse: r.warehouse,
                createdAt: r.created_at,
                batchIndex: 1,
                batchTotal: 1,
                dailySeq: r.daily_seq,
                operator: r.created_by_name ?? undefined,
                stockType: r.stock_type ?? undefined,
                supplier: r.supplier_name ?? undefined,
            });
            const saved = getSavedNetworkPrinter();
            let ok = false;
            if (saved) ok = (await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, r.pallet_id)).success;
            const name = inboundLabelName(r.stock_type, r.supplier_name);
            if (!ok) ok = (await silentPrintBarcode(r.pallet_id, 1, { ...(name ? { name } : {}), fallbackHtml: html })).success;
            if (!ok) ok = (await silentPrint(html)).success;
            if (!ok) ok = (await browserPrint(html)).success;
            if (ok) toast.success(t("inbound_admin_reprinted"), { toastId: "inbound-admin-reprint" });
            else toast.error(t("print_failed"), { toastId: "inbound-admin-reprint" });
        } finally {
            setBusy(null);
        }
    };

    const voidLabel = async (r: PalletRow) => {
        if (!window.confirm(t("inbound_admin_void_confirm", { pallet: r.pallet_id }))) return;
        setBusy(r.pallet_id);
        try {
            const res = await post(`/pallet-receiving/void`, { palletId: r.pallet_id });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                toast.error(data?.reason === "cannot_void_placed" ? t("cannot_void_placed") : t("server_error"), { toastId: "inbound-admin-void" });
                return;
            }
            toast.info(t("pallet_voided"), { toastId: "inbound-admin-void" });
            setHistory(null);
            await load();
        } finally {
            setBusy(null);
        }
    };

    const forceProcess = async (r: PalletRow) => {
        if (!window.confirm(t("inbound_admin_force_process_confirm", { pallet: r.pallet_id }))) return;
        setBusy(r.pallet_id);
        try {
            const res = await post(`/pallet-receiving/admin/pallets/${encodeURIComponent(r.pallet_id)}/force-process`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setHistory(null);
            await load();
        } catch {
            toast.error(t("server_error"), { toastId: "inbound-admin-fp" });
        } finally {
            setBusy(null);
        }
    };

    const submitFound = async () => {
        if (!foundPrompt) return;
        const loc = foundLocation.trim();
        if (!loc) return;
        setBusy(foundPrompt.pallet_id);
        try {
            const res = await post(`/pallet-receiving/admin/pallets/${encodeURIComponent(foundPrompt.pallet_id)}/found`, { locationId: loc });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                toast.error(data?.reason === "wrong_type" || data?.reason === "invalid_location" || res.status === 404
                    ? t("invalid_location")
                    : t("server_error"), { toastId: "inbound-admin-found" });
                return;
            }
            toast.success(t("inbound_admin_found_done", {
                pallet: foundPrompt.pallet_id,
                location: data?.location?.display_name || data?.location?.barcode || loc,
            }), { toastId: "inbound-admin-found" });
            setFoundPrompt(null);
            setFoundLocation("");
            setHistory(null);
            await load();
        } finally {
            setBusy(null);
        }
    };

    const exportCsv = async () => {
        if (!warehouse) return;
        setExporting(true);
        try {
            const res = await apiFetch(`/pallet-receiving/admin/pallets/export?${buildQuery()}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `inbound-pallets-${warehouse}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t("server_error"), { toastId: "inbound-admin-export" });
        } finally {
            setExporting(false);
        }
    };

    // ---- Derived -----------------------------------------------------------

    const grouped = useMemo(() => {
        if (!groupByLocation) return null;
        const map = new Map<string, { key: string; name: string; rows: PalletRow[] }>();
        for (const r of rows) {
            const key = r.location_id ?? "";
            if (!map.has(key)) map.set(key, { key, name: r.location_name || r.location_id || t("inbound_admin_no_location"), rows: [] });
            map.get(key)!.rows.push(r);
        }
        return Array.from(map.values()).sort((a, b) => b.rows.length - a.rows.length || a.name.localeCompare(b.name));
    }, [rows, groupByLocation, t]);

    const statusOptions = (["pending", "placed", "missing", "processed", "voided"] as const)
        .map(s => ({ value: s, label: t(`inbound_admin_status_${s}`) }));
    const typeOptions = stockTypes.map(st => ({ value: st.name, label: st.name }));

    const oldestLabel = (() => {
        if (!summary?.oldest_placed_at) return "—";
        const ms = Date.now() - new Date(summary.oldest_placed_at).getTime();
        return ms < DAY
            ? t("inbound_admin_age_hours", { hours: String(Math.floor(ms / HOUR)) })
            : t("inbound_admin_age_days", { days: String(Math.floor(ms / DAY)) });
    })();

    const renderRow = (r: PalletRow) => (
        <tr
            key={r.pallet_id}
            className={`inbound-admin-row${ageClass(r)}${statusOf(r) === "missing" ? " inbound-admin-row--missing" : ""}`}
            onClick={() => openHistory(r)}
        >
            <td className="locations-table-mono">{r.pallet_id}</td>
            <td>{r.stock_type || "—"}</td>
            <td>{r.supplier_name || "—"}</td>
            <td><span className={`inbound-admin-status inbound-admin-status--${statusOf(r)}`}>{statusLabel(r)}</span></td>
            <td>
                {r.status === "processed"
                    ? (r.processed_location_name || r.processed_location_id || "—")
                    : (r.location_name || r.location_id || "—")}
            </td>
            <td>{fmtWhen(r.created_at)}</td>
            <td className="locations-table-qty">{r.status === "placed" || r.status === "pending" ? ageLabel(r) : "—"}</td>
            <td>{r.status === "placed" ? fmtWhen(r.location_since) : "—"}</td>
            <td>{fmtWhen(r.last_seen_at)}</td>
            <td>{r.last_seen_by_name || r.created_by_name || "—"}</td>
            <td className="inbound-admin-row-chevron"><ChevronRight size={16} /></td>
        </tr>
    );

    const tableHead = (
        <thead>
            <tr>
                <th>{t("inbound_admin_col_pallet")}</th>
                <th>{t("inbound_admin_col_type")}</th>
                <th>{t("inbound_admin_col_supplier")}</th>
                <th>{t("inbound_admin_col_status")}</th>
                <th>{t("inbound_admin_col_location")}</th>
                <th>{t("inbound_admin_col_printed")}</th>
                <th className="locations-table-qty">{t("inbound_admin_col_age")}</th>
                <th>{t("inbound_admin_col_at_location")}</th>
                <th>{t("inbound_admin_col_last_seen")}</th>
                <th>{t("inbound_admin_col_by")}</th>
                <th />
            </tr>
        </thead>
    );

    return (
        <div className="inbound-admin">
            {/* Warehouse tabs */}
            <FilterPills
                className="warehouse-admin-pills inbound-admin-warehouse-pills"
                value={warehouse}
                onChange={w => { if (w) setWarehouse(w); }}
                options={warehouseScope.limit(warehouses).map(w => ({ value: w, label: w }))}
                hideAll={!warehouseScope.canCross}
                allLabelKey="warehouse_col"
            />

            {/* Summary tiles */}
            <div className="inbound-admin-tiles">
                <button type="button" className={`inbound-admin-tile${statusFilter === "pending" ? " inbound-admin-tile--active" : ""}`} onClick={() => setStatusFilter(s => s === "pending" ? "" : "pending")}>
                    <span className="inbound-admin-tile-num">{summary?.pending ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("inbound_admin_tile_pending")}</span>
                </button>
                <div className="inbound-admin-tile">
                    <span className="inbound-admin-tile-num">{summary?.in_lanes ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("inbound_admin_tile_in_lanes")}</span>
                </div>
                <div className="inbound-admin-tile">
                    <span className="inbound-admin-tile-num">{summary?.in_storage ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("inbound_admin_tile_in_storage")}</span>
                </div>
                <button type="button" className={`inbound-admin-tile inbound-admin-tile--missing${statusFilter === "missing" ? " inbound-admin-tile--active" : ""}`} onClick={() => setStatusFilter(s => s === "missing" ? "" : "missing")}>
                    <span className="inbound-admin-tile-num">{summary?.missing ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("inbound_admin_tile_missing")}</span>
                </button>
                <div className="inbound-admin-tile">
                    <span className="inbound-admin-tile-num">{summary?.processed_today ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("inbound_admin_tile_processed_today")}</span>
                </div>
                <div className="inbound-admin-tile">
                    <span className="inbound-admin-tile-num">{oldestLabel}</span>
                    <span className="inbound-admin-tile-label">{t("inbound_admin_tile_oldest")}</span>
                </div>
            </div>

            {/* Type + status pills */}
            {typeOptions.length > 0 && (
                <FilterPills
                    className="warehouse-admin-pills"
                    value={typeFilter}
                    onChange={setTypeFilter}
                    options={typeOptions}
                    labelKey="inbound_admin_col_type"
                />
            )}
            <FilterPills
                className="warehouse-admin-pills"
                value={statusFilter}
                onChange={v => setStatusFilter(v as StatusFilter)}
                options={statusOptions}
                labelKey="inbound_admin_col_status"
                trailing={(
                    <div className="inbound-admin-actions">
                        <label className="inbound-admin-toggle">
                            <input type="checkbox" checked={groupByLocation} onChange={e => setGroupByLocation(e.target.checked)} />
                            <span>{t("inbound_admin_group_by_location")}</span>
                        </label>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={exportCsv} disabled={exporting || rows.length === 0}>
                            <Download size={14} /> {exporting ? t("inventory_exporting") : t("inbound_admin_export")}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
                            <RefreshCw size={14} /> {t("refresh")}
                        </button>
                    </div>
                )}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("inbound_admin_search_hint")}
                    value={q}
                    onChange={e => setQ(e.target.value)}
                />
            </div>

            <div className="locations-table-wrap">
                {rows.length === 0 ? (
                    <div className="locations-table-empty">{loading ? t("loading") : t("inbound_admin_none")}</div>
                ) : grouped ? (
                    grouped.map(g => (
                        <div key={g.key || "__none"} className="inbound-admin-group">
                            <div className="inbound-admin-group-head">
                                <MapPin size={16} />
                                <strong>{g.name}</strong>
                                <span className="inbound-admin-group-count">{t("inbound_admin_pallets_here", { count: String(g.rows.length) })}</span>
                            </div>
                            <table className="locations-table inbound-admin-table">
                                {tableHead}
                                <tbody>{g.rows.map(renderRow)}</tbody>
                            </table>
                        </div>
                    ))
                ) : (
                    <table className="locations-table inbound-admin-table">
                        {tableHead}
                        <tbody>{rows.map(renderRow)}</tbody>
                    </table>
                )}
            </div>

            {/* History modal */}
            {history && (
                <div className="modal-overlay" onClick={() => setHistory(null)}>
                    <div className="modal-card inbound-admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>
                                <Package size={18} /> {history.pallet.pallet_id}
                                {history.pallet.stock_type && <span className="inbound-admin-type-tag">{history.pallet.stock_type}</span>}
                                {history.pallet.supplier_name && <span className="inbound-admin-type-tag inbound-admin-supplier-tag">{history.pallet.supplier_name}</span>}
                            </h2>
                            <button className="modal-close" onClick={() => setHistory(null)} aria-label={t("cancel")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="inbound-admin-history-meta">
                                <span className={`inbound-admin-status inbound-admin-status--${statusOf(history.pallet)}`}>{statusLabel(history.pallet)}</span>
                                {history.pallet.status === "placed" && (
                                    <span>{history.pallet.location_name || history.pallet.location_id} · {ageLabel(history.pallet)}</span>
                                )}
                            </div>
                            <h3 className="inbound-admin-history-title">{t("inbound_admin_history_title")}</h3>
                            <ol className="inbound-admin-timeline">
                                {history.events.map((ev, i) => {
                                    const text = ev.kind === "printed" ? t("inbound_admin_event_printed")
                                        : ev.kind === "placed" ? t("inbound_admin_event_placed", { location: ev.location_name || ev.location_id || "?" })
                                        : ev.kind === "moved" ? t("inbound_admin_event_moved", { from: ev.from_location_name || ev.from_location_id || "?", to: ev.location_name || ev.location_id || "?" })
                                        : ev.kind === "missing" ? t("inbound_admin_event_missing", { location: ev.location_name || ev.location_id || "?" })
                                        : ev.kind === "processed" ? t("inbound_admin_event_processed")
                                        : t("inbound_admin_event_voided");
                                    return (
                                        <li key={i} className={`inbound-admin-timeline-item inbound-admin-timeline-item--${ev.kind}`}>
                                            <span className="inbound-admin-timeline-when">{fmtWhen(ev.at)}</span>
                                            <span className="inbound-admin-timeline-text">{text}</span>
                                            <span className="inbound-admin-timeline-meta">
                                                {ev.source && <span>{t(`inbound_admin_source_${ev.source}`)}</span>}
                                                {ev.by_name && <span>{ev.by_name}</span>}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                            <div className="inbound-admin-history-actions">
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => reprint(history.pallet)} disabled={busy === history.pallet.pallet_id || history.pallet.status === "voided"}>
                                    <Printer size={14} /> {t("inbound_admin_action_reprint")}
                                </button>
                                {history.pallet.status === "pending" && (
                                    <button type="button" className="btn btn-ghost btn-sm inbound-admin-danger" onClick={() => voidLabel(history.pallet)} disabled={busy === history.pallet.pallet_id || readOnly} aria-disabled={readOnly} title={readOnly ? t("read_only_mode") : undefined}>
                                        <Trash2 size={14} /> {t("inbound_admin_action_void")}
                                    </button>
                                )}
                                {history.pallet.status === "placed" && (
                                    <>
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFoundPrompt(history.pallet); setFoundLocation(""); }} disabled={busy === history.pallet.pallet_id || readOnly} aria-disabled={readOnly} title={readOnly ? t("read_only_mode") : undefined}>
                                            <MapPin size={14} /> {t("inbound_admin_action_found")}
                                        </button>
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => forceProcess(history.pallet)} disabled={busy === history.pallet.pallet_id || readOnly} aria-disabled={readOnly} title={readOnly ? t("read_only_mode") : undefined}>
                                            <CheckCircle2 size={14} /> {t("inbound_admin_action_force_process")}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mark found at… */}
            {foundPrompt && (
                <div className="modal-overlay" onClick={() => setFoundPrompt(null)}>
                    <div className="modal-card inbound-admin-modal inbound-admin-modal--small" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("inbound_admin_action_found")}</h2>
                            <button className="modal-close" onClick={() => setFoundPrompt(null)} aria-label={t("cancel")}>
                                <X size={18} />
                            </button>
                        </div>
                        <form className="modal-card-body" onSubmit={e => { e.preventDefault(); submitFound(); }}>
                            <p className="admin-settings-section-hint">{t("inbound_admin_found_prompt", { pallet: foundPrompt.pallet_id })}</p>
                            <input
                                ref={foundInputRef}
                                className="locations-table-search"
                                value={foundLocation}
                                onChange={e => setFoundLocation(e.target.value)}
                                placeholder="P7L9"
                            />
                            <div className="modal-confirm-buttons">
                                <button type="button" className="btn btn-ghost btn-lg" onClick={() => setFoundPrompt(null)}>{t("cancel")}</button>
                                <button type="submit" className="btn btn-primary btn-lg" disabled={!foundLocation.trim() || busy === foundPrompt.pallet_id || readOnly} aria-disabled={readOnly} title={readOnly ? t("read_only_mode") : undefined}>
                                    {busy === foundPrompt.pallet_id ? t("submitting") : t("yes")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InboundAdmin;
